/**
 * Shared create/edit form for articleCategories & articlePetTags (admin).
 */

import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import CustomModal from "../../shared/customModal/customModal";
import {
  Button,
  Div,
  FeedbackFormInput,
  H1,
  P,
} from "../../../styles/styles";

const LANGUAGES = ["en", "ru", "sr"];

export const LOOKUP_CREATE_FORM_ID = "lookup-item-create-form";

export function lookupEditFormId(itemId) {
  return `lookup-item-edit-form-${itemId}`;
}

export default function AdminLookupFormPanel({
  title,
  formId,
  formData,
  setFormData,
  isEdit,
  onCancel,
  onSubmit,
  onDeleted,
  saving,
  deleting,
}) {
  const isDesktop = useMediaQuery({ minWidth: "800px" });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const busy = saving || deleting;

  const patchLang = (field, lang, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleted) return;
    await onDeleted();
    setConfirmDeleteOpen(false);
  };

  return (
    <Div
      width="100%"
      padding="20px"
      borderRadius="10px"
      backWhite
      grayBorder
      style={{ marginTop: 12, boxSizing: "border-box" }}
    >
      <H1 fontSize="20px" margin="0 0 15px 0">
        {title}
      </H1>

      <form id={formId} onSubmit={handleSubmit} style={{ width: "100%" }}>
        <Div gap="15px" width="100%">
          {!isEdit ? (
            <Div width="100%" alignStart gap="8px">
              <P textWidth="auto" fontWeight="600" margin="0" textAlign="left">
                Document ID (optional)
              </P>
              <FeedbackFormInput
                value={formData.id}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, id: e.target.value }))
                }
                placeholder="health"
                autoComplete="off"
                style={{ padding: "3px 5px", border: "1px gray solid" }}
              />
              <P margin="0" fontSize="12px" textAlign="left" style={{ color: "#666" }}>
                Letters, digits, hyphen, underscore. Auto-generated if empty.
              </P>
            </Div>
          ) : (
            <Div width="100%" alignStart>
              <P fontWeight="600" margin="0" textAlign="left">
                ID:
                {" "}
                {formData.id}
              </P>
            </Div>
          )}

          <Div width="100%" alignStart gap="8px">
            <P textWidth="auto" fontWeight="600" margin="0" textAlign="left">
              Order
            </P>
            <FeedbackFormInput
              type="number"
              value={formData.order === 0 ? "" : formData.order}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  order: Number(e.target.value) || 0,
                }))
              }
              placeholder="0"
              style={{ padding: "3px 5px", border: "1px gray solid", maxWidth: 120 }}
            />
          </Div>

          <Div
            width="100%"
            row={isDesktop}
            gap={isDesktop ? "12px" : "15px"}
            alignStart
            justifyStart
            wrap={!isDesktop ? "wrap" : "nowrap"}
          >
            {LANGUAGES.map((lang) => (
              <Div
                key={lang}
                width={isDesktop ? undefined : "100%"}
                padding="15px"
                borderRadius="8px"
                style={{
                  backgroundColor: "#f5f5f5",
                  flex: isDesktop ? "1 1 0" : undefined,
                  minWidth: isDesktop ? 0 : undefined,
                  boxSizing: "border-box",
                }}
              >
                <P
                  fontSize="16px"
                  fontWeight="600"
                  margin="0 0 10px 0"
                  textAlign="left"
                >
                  {lang.toUpperCase()}
                </P>
                <Div gap="10px">
                  <Div>
                    <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
                      Name
                    </P>
                    <FeedbackFormInput
                      value={formData.name[lang] || ""}
                      onChange={(e) => patchLang("name", lang, e.target.value)}
                      placeholder={`Name (${lang})`}
                      style={{ padding: "3px 5px", border: "1px gray solid" }}
                    />
                  </Div>
                  <Div>
                    <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
                      Slug (optional)
                    </P>
                    <FeedbackFormInput
                      value={formData.slug[lang] || ""}
                      onChange={(e) => patchLang("slug", lang, e.target.value)}
                      placeholder={`slug-${lang}`}
                      style={{ padding: "3px 5px", border: "1px gray solid" }}
                    />
                  </Div>
                </Div>
              </Div>
            ))}
          </Div>

          <Div row gap="10px" wrap="wrap" justifyStart>
            <Button type="submit" greenButton width="auto" disabled={busy}>
              {saving ? "Saving…" : isEdit ? "Update" : "Create"}
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              width="auto"
              outlineButton
              disabled={busy}
            >
              Cancel
            </Button>
            {isEdit && onDeleted ? (
              <Button
                type="button"
                deleteButton
                width="auto"
                disabled={busy}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete
              </Button>
            ) : null}
          </Div>
        </Div>
      </form>

      {isEdit && onDeleted ? (
        <CustomModal
          open={confirmDeleteOpen}
          onClose={() => !deleting && setConfirmDeleteOpen(false)}
          title="Delete item?"
        >
          <P style={{ marginBottom: 20 }}>
            This entry will be permanently removed. Articles may keep stale
            references in categoryIds or petTagIds.
          </P>
          <Div row gap="10px" justifyEnd>
            <Button
              type="button"
              temporaryButton
              fontWeight="400"
              width="auto"
              disabled={deleting}
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              deleteButton
              width="auto"
              disabled={deleting}
              onClick={handleDeleteConfirm}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </Div>
        </CustomModal>
      ) : null}
    </Div>
  );
}
